/**
 * ============================================================================
 * CALENDAR PDF EXPORT v1.0
 * ============================================================================
 *
 * Exportiert Kalender-Termine als PDF
 * - Monatsansicht: Alle Termine eines Monats als Tabelle
 * - Wochenansicht: Alle Termine einer Woche als Tabelle
 *
 * Dependencies: jspdf, jspdf-autotable
 *
 * @version 1.0.0
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// ============================================================================
// CONSTANTS
// ============================================================================
const BRAND_COLOR = [74, 144, 164]; // Planivio Blue RGB
const HEADER_COLOR = [249, 115, 22]; // Orange RGB

const MONTH_NAMES_DE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

const DAY_NAMES_DE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const CATEGORY_LABELS = {
  arbeit: 'Arbeit',
  familie: 'Familie',
  gesundheit: 'Gesundheit',
  freizeit: 'Freizeit',
  haushalt: 'Haushalt',
  finanzen: 'Finanzen',
  bildung: 'Bildung',
  sonstiges: 'Sonstiges'
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Extrahiert das Start-Datum eines Termins
 * Unterstützt beide Formate: start_time (ISO) oder date + time (separate Felder)
 */
function getAppointmentDateTime(apt) {
  // Wenn start_time existiert und ein gültiges Datum ist
  if (apt.start_time) {
    const d = new Date(apt.start_time);
    if (!isNaN(d.getTime())) {
      return d;
    }
  }

  // Fallback: date + time Felder kombinieren
  if (apt.date) {
    const dateStr = apt.date;
    const timeStr = apt.time || '00:00';
    const combined = `${dateStr}T${timeStr}`;
    const d = new Date(combined);
    if (!isNaN(d.getTime())) {
      return d;
    }
  }

  // Letzter Fallback
  return null;
}

/**
 * Formatiert ein Datum für die Anzeige
 */
function formatDate(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
}

/**
 * Formatiert eine Uhrzeit für die Anzeige
 */
function formatTime(dateString) {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Formatiert Datum und Uhrzeit zusammen
 */
function formatDateTime(dateString) {
  if (!dateString) return '-';
  return `${formatDate(dateString)} ${formatTime(dateString)}`;
}

/**
 * Gibt den deutschen Wochentag zurück
 */
function getDayName(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return DAY_NAMES_DE[date.getDay()];
}

/**
 * Sortiert Termine nach Startzeit
 */
function sortAppointments(appointments) {
  return [...appointments].sort((a, b) => {
    const dateA = getAppointmentDateTime(a) || new Date(0);
    const dateB = getAppointmentDateTime(b) || new Date(0);
    return dateA - dateB;
  });
}

/**
 * Kürzt Text auf maximale Länge
 */
function truncateText(text, maxLength = 40) {
  if (!text) return '-';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - 3) + '...';
}

// ============================================================================
// EXPORT FUNCTIONS
// ============================================================================

/**
 * Exportiert alle Termine eines Monats als PDF
 *
 * @param {number} year - Jahr (z.B. 2024)
 * @param {number} month - Monat (0-11)
 * @param {Array} appointments - Array mit Terminen
 * @param {Object} options - Optionale Einstellungen
 */
export function exportMonthToPDF(year, month, appointments, options = {}) {
  console.log('📄 Exporting month to PDF:', year, month, appointments.length, 'appointments');

  const doc = new jsPDF();
  const monthName = MONTH_NAMES_DE[month];

  // Filter appointments for this month
  const monthAppointments = appointments.filter(apt => {
    const aptDate = getAppointmentDateTime(apt);
    if (!aptDate) return false;
    return aptDate.getFullYear() === year && aptDate.getMonth() === month;
  });

  console.log('📄 Filtered month appointments:', monthAppointments.length);

  const sortedAppointments = sortAppointments(monthAppointments);

  // ============================================
  // HEADER
  // ============================================
  doc.setFillColor(...HEADER_COLOR);
  doc.rect(0, 0, 210, 25, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(`Kalender ${monthName} ${year}`, 14, 16);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Planivio - ${sortedAppointments.length} Termine`, 196, 16, { align: 'right' });

  // ============================================
  // TABLE
  // ============================================
  if (sortedAppointments.length === 0) {
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(12);
    doc.text('Keine Termine in diesem Monat.', 14, 45);
  } else {
    const tableData = sortedAppointments.map(apt => {
      const aptDate = getAppointmentDateTime(apt);
      const dateStr = aptDate ? aptDate.toISOString() : null;
      return [
        getDayName(dateStr),
        formatDate(dateStr),
        formatTime(dateStr),
        truncateText(apt.title, 35),
        CATEGORY_LABELS[apt.category] || apt.type || apt.category || '-',
        apt.person || '-'
      ];
    });

    autoTable(doc, {
      head: [['Tag', 'Datum', 'Uhrzeit', 'Titel', 'Kategorie', 'Person']],
      body: tableData,
      startY: 35,
      styles: {
        fontSize: 9,
        cellPadding: 3,
        overflow: 'ellipsize'
      },
      headStyles: {
        fillColor: BRAND_COLOR,
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      columnStyles: {
        0: { cellWidth: 12 },  // Tag
        1: { cellWidth: 25 },  // Datum
        2: { cellWidth: 18 },  // Uhrzeit
        3: { cellWidth: 'auto' }, // Titel
        4: { cellWidth: 28 },  // Kategorie
        5: { cellWidth: 30 }   // Person
      }
    });
  }

  // ============================================
  // FOOTER
  // ============================================
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Erstellt am ${formatDate(new Date().toISOString())} | Seite ${i} von ${pageCount}`,
      105,
      290,
      { align: 'center' }
    );
  }

  // ============================================
  // SAVE
  // ============================================
  const filename = `Planivio-Kalender-${year}-${String(month + 1).padStart(2, '0')}.pdf`;
  doc.save(filename);

  console.log('📄 PDF saved:', filename);
  return filename;
}

/**
 * Exportiert alle Termine einer Woche als PDF
 *
 * @param {Date} weekStart - Erster Tag der Woche (Montag)
 * @param {Array} appointments - Array mit Terminen
 * @param {Object} options - Optionale Einstellungen
 */
export function exportWeekToPDF(weekStart, appointments, options = {}) {
  const startDate = new Date(weekStart);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 6);
  endDate.setHours(23, 59, 59, 999);

  console.log('📄 Exporting week to PDF:', formatDate(startDate.toISOString()), '-', formatDate(endDate.toISOString()));
  console.log('📄 Total appointments received:', appointments.length);

  // Debug: Log first appointment structure
  if (appointments.length > 0) {
    console.log('📄 Sample appointment structure:', {
      start_time: appointments[0].start_time,
      date: appointments[0].date,
      time: appointments[0].time,
      title: appointments[0].title
    });
  }

  const doc = new jsPDF();

  // Filter appointments for this week - compare dates properly
  const weekAppointments = appointments.filter(apt => {
    const aptDate = getAppointmentDateTime(apt);
    if (!aptDate) {
      console.log('📄 Skipping (no valid date):', apt.title);
      return false;
    }
    const isInRange = aptDate >= startDate && aptDate <= endDate;
    if (isInRange) {
      console.log('📄 Including:', apt.title, aptDate.toISOString());
    }
    return isInRange;
  });

  console.log('📄 Filtered week appointments:', weekAppointments.length);

  const sortedAppointments = sortAppointments(weekAppointments);

  // ============================================
  // HEADER
  // ============================================
  doc.setFillColor(...HEADER_COLOR);
  doc.rect(0, 0, 210, 25, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(`Wochenübersicht`, 14, 12);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`${formatDate(startDate.toISOString())} - ${formatDate(endDate.toISOString())}`, 14, 20);

  doc.setFontSize(10);
  doc.text(`Planivio - ${sortedAppointments.length} Termine`, 196, 16, { align: 'right' });

  // ============================================
  // TABLE
  // ============================================
  if (sortedAppointments.length === 0) {
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(12);
    doc.text('Keine Termine in dieser Woche.', 14, 45);
  } else {
    const tableData = sortedAppointments.map(apt => {
      const aptDate = getAppointmentDateTime(apt);
      const dateStr = aptDate ? aptDate.toISOString() : null;
      return [
        getDayName(dateStr),
        formatDate(dateStr),
        formatTime(dateStr),
        truncateText(apt.title, 35),
        CATEGORY_LABELS[apt.category] || apt.type || apt.category || '-',
        apt.person || '-'
      ];
    });

    autoTable(doc, {
      head: [['Tag', 'Datum', 'Uhrzeit', 'Titel', 'Kategorie', 'Person']],
      body: tableData,
      startY: 35,
      styles: {
        fontSize: 9,
        cellPadding: 3,
        overflow: 'ellipsize'
      },
      headStyles: {
        fillColor: BRAND_COLOR,
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      columnStyles: {
        0: { cellWidth: 12 },
        1: { cellWidth: 25 },
        2: { cellWidth: 18 },
        3: { cellWidth: 'auto' },
        4: { cellWidth: 28 },
        5: { cellWidth: 30 }
      }
    });
  }

  // ============================================
  // FOOTER
  // ============================================
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Erstellt am ${formatDate(new Date().toISOString())} | Seite ${i} von ${pageCount}`,
      105,
      290,
      { align: 'center' }
    );
  }

  // ============================================
  // SAVE
  // ============================================
  const year = startDate.getFullYear();
  const weekNum = getWeekNumber(startDate);
  const filename = `Planivio-Woche-${year}-KW${String(weekNum).padStart(2, '0')}.pdf`;
  doc.save(filename);

  console.log('📄 PDF saved:', filename);
  return filename;
}

/**
 * Berechnet die Kalenderwoche
 */
function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

/**
 * Exportiert einen einzelnen Tag als PDF
 *
 * @param {Date} date - Das Datum
 * @param {Array} appointments - Array mit Terminen
 */
export function exportDayToPDF(date, appointments) {
  const targetDate = new Date(date);
  targetDate.setHours(0, 0, 0, 0);

  console.log('📄 Exporting day to PDF:', formatDate(targetDate.toISOString()));

  const doc = new jsPDF();

  // Filter appointments for this day
  const dayAppointments = appointments.filter(apt => {
    const aptDate = getAppointmentDateTime(apt);
    if (!aptDate) return false;
    const aptDateOnly = new Date(aptDate);
    aptDateOnly.setHours(0, 0, 0, 0);
    return aptDateOnly.getTime() === targetDate.getTime();
  });

  console.log('📄 Filtered day appointments:', dayAppointments.length);

  const sortedAppointments = sortAppointments(dayAppointments);

  // ============================================
  // HEADER
  // ============================================
  doc.setFillColor(...HEADER_COLOR);
  doc.rect(0, 0, 210, 25, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');

  const dayName = DAY_NAMES_DE[targetDate.getDay()];
  const monthName = MONTH_NAMES_DE[targetDate.getMonth()];
  doc.text(`${dayName}, ${targetDate.getDate()}. ${monthName} ${targetDate.getFullYear()}`, 14, 16);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Planivio - ${sortedAppointments.length} Termine`, 196, 16, { align: 'right' });

  // ============================================
  // TABLE
  // ============================================
  if (sortedAppointments.length === 0) {
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(12);
    doc.text('Keine Termine an diesem Tag.', 14, 45);
  } else {
    const tableData = sortedAppointments.map(apt => {
      const aptDate = getAppointmentDateTime(apt);
      const dateStr = aptDate ? aptDate.toISOString() : null;
      return [
        formatTime(dateStr),
        apt.end_time ? formatTime(apt.end_time) : '-',
        truncateText(apt.title, 40),
        CATEGORY_LABELS[apt.category] || apt.type || apt.category || '-',
        apt.person || '-',
        truncateText(apt.location || '-', 25)
      ];
    });

    autoTable(doc, {
      head: [['Von', 'Bis', 'Titel', 'Kategorie', 'Person', 'Ort']],
      body: tableData,
      startY: 35,
      styles: {
        fontSize: 9,
        cellPadding: 3,
        overflow: 'ellipsize'
      },
      headStyles: {
        fillColor: BRAND_COLOR,
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      columnStyles: {
        0: { cellWidth: 18 },
        1: { cellWidth: 18 },
        2: { cellWidth: 'auto' },
        3: { cellWidth: 28 },
        4: { cellWidth: 30 },
        5: { cellWidth: 30 }
      }
    });
  }

  // ============================================
  // FOOTER
  // ============================================
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text(
    `Erstellt am ${formatDate(new Date().toISOString())}`,
    105,
    290,
    { align: 'center' }
  );

  // ============================================
  // SAVE
  // ============================================
  const filename = `Planivio-Tag-${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}-${String(targetDate.getDate()).padStart(2, '0')}.pdf`;
  doc.save(filename);

  console.log('📄 PDF saved:', filename);
  return filename;
}

export default {
  exportMonthToPDF,
  exportWeekToPDF,
  exportDayToPDF
};
